// ControlAssessmentsTab — shows the 4 effectiveness assessment cards for a control
// grouped by category (Self-Assessed / Independent), with inline recording.
// All 4 assessment types always render even if never assessed (state: not_assessed).

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { controlsApi } from '@/lib/api/controls'
import type { ControlAssessment, RecordAssessmentRequest, AssessmentHistoryEntry } from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AlertTriangle, CheckCircle2, Clock, History, MinusCircle, CircleDot } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Layout: two groups of two ---

const assessmentGroups = [
  {
    title: 'Self-Assessed',
    description: 'Assessments performed by the control owner or first line.',
    types: ['self_assessed_design', 'self_assessed_operating'],
  },
  {
    title: 'Independent',
    description: 'Assessments performed by an independent reviewer (second/third line).',
    types: ['independent_design', 'independent_operating'],
  },
] as const

// --- Labels ---

const assessmentTypeLabels: Record<string, string> = {
  self_assessed_design: 'Design Effectiveness',
  self_assessed_operating: 'Operating Effectiveness',
  independent_design: 'Design Effectiveness',
  independent_operating: 'Operating Effectiveness',
}

const assessmentTypeFullLabels: Record<string, string> = {
  self_assessed_design: 'Self-Assessed Design',
  self_assessed_operating: 'Self-Assessed Operating',
  independent_design: 'Independent Design',
  independent_operating: 'Independent Operating',
}

const assessmentTypeDescriptions: Record<string, string> = {
  self_assessed_design: 'Is the control designed to address the risk effectively?',
  self_assessed_operating: 'Is the control operating as designed in practice?',
  independent_design: 'Independent review of control design adequacy.',
  independent_operating: 'Independent review of control operating effectiveness.',
}

const effectivenessLabels: Record<string, string> = {
  not_assessed: 'Not Assessed',
  effective: 'Effective',
  partially_effective: 'Partially Effective',
  ineffective: 'Ineffective',
}

const effectivenessOptions = ['effective', 'partially_effective', 'ineffective'] as const

function effectivenessIcon(state: string) {
  switch (state) {
    case 'effective':
      return <CheckCircle2 className="h-5 w-5 text-green-600" />
    case 'partially_effective':
      return <MinusCircle className="h-5 w-5 text-amber-600" />
    case 'ineffective':
      return <AlertTriangle className="h-5 w-5 text-red-600" />
    default:
      return <CircleDot className="h-5 w-5 text-gray-400" />
  }
}

function effectivenessSmallIcon(state: string) {
  switch (state) {
    case 'effective':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'partially_effective':
      return <MinusCircle className="h-4 w-4 text-amber-600" />
    case 'ineffective':
      return <AlertTriangle className="h-4 w-4 text-red-600" />
    default:
      return <CircleDot className="h-4 w-4 text-gray-400" />
  }
}

function effectivenessBadgeStyle(state: string): string {
  switch (state) {
    case 'effective':
      return 'bg-green-100 text-green-700'
    case 'partially_effective':
      return 'bg-amber-100 text-amber-700'
    case 'ineffective':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-500'
  }
}

export function ControlAssessmentsTab() {
  const { slug } = useTenant()
  const { controlId } = useParams({ strict: false }) as { controlId: string }
  const api = useApi()
  const ctrlApi = controlsApi(api)
  const [historyOpen, setHistoryOpen] = useState(false)

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.controls.assessments(slug, controlId),
    queryFn: () => ctrlApi.assessments(controlId),
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load assessments"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  // Build a lookup by type — always have all 4
  const byType = new Map<string, ControlAssessment>()
  if (data) {
    for (const a of data) {
      byType.set(a.assessment_type, a)
    }
  }

  // Summary stats
  const assessed = data?.filter((a) => a.effectiveness_state !== 'not_assessed').length ?? 0
  const overdue = data?.filter((a) => a.is_overdue).length ?? 0

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Effectiveness Assessments</h2>
          <p className="text-sm text-muted-foreground">
            Four assessment dimensions track whether this control is designed and operating effectively.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {assessed}/4 assessed
          </span>
          {overdue > 0 && (
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {overdue} overdue
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}>
            <History className="mr-1.5 h-3.5 w-3.5" />
            View Assessment History
          </Button>
        </div>
      </div>

      {assessmentGroups.map((group) => (
        <div key={group.title} className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {group.title}
            </h3>
            <p className="text-xs text-muted-foreground">{group.description}</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {group.types.map((type) => (
              <AssessmentCard
                key={type}
                assessmentType={type}
                assessment={byType.get(type) ?? null}
                controlId={controlId}
                slug={slug}
              />
            ))}
          </div>
        </div>
      ))}

      <AssessmentHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        controlId={controlId}
        slug={slug}
      />
    </div>
  )
}

// --- Assessment card with inline recording ---

function AssessmentCard({
  assessmentType,
  assessment,
  controlId,
  slug,
}: {
  assessmentType: string
  assessment: ControlAssessment | null
  controlId: string
  slug: string
}) {
  const [recording, setRecording] = useState(false)

  const state = assessment?.effectiveness_state ?? 'not_assessed'
  const isNotAssessed = state === 'not_assessed'

  return (
    <div className={cn('rounded-lg border', assessment?.is_overdue && 'border-blue-200')}>
      {/* Card header */}
      <div className="flex items-start justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2.5">
          {effectivenessIcon(state)}
          <div>
            <h3 className="text-sm font-semibold">
              {assessmentTypeLabels[assessmentType] ?? assessmentType}
            </h3>
            <p className="text-xs text-muted-foreground">
              {assessmentTypeDescriptions[assessmentType] ?? ''}
            </p>
          </div>
        </div>
        {assessment?.is_overdue && (
          <Badge variant="outline" className="shrink-0 border-blue-200 bg-blue-50 text-blue-700">
            <AlertTriangle className="mr-1 h-3 w-3" />
            Overdue
          </Badge>
        )}
      </div>

      {/* Card body */}
      <div className="space-y-3 px-4 py-3">
        {/* Current state row */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Current rating</span>
          <Badge variant="secondary" className={cn('text-xs', effectivenessBadgeStyle(state))}>
            {effectivenessLabels[state] ?? state}
          </Badge>
        </div>

        {/* Last assessed row */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Last assessed</span>
          {assessment?.assessed_date ? (
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              {new Date(assessment.assessed_date).toLocaleDateString()}
              {assessment.days_since_assessed != null && (
                <span className="text-xs text-muted-foreground">
                  ({assessment.days_since_assessed}d ago)
                </span>
              )}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground italic">Never</span>
          )}
        </div>

        {/* Assessed by */}
        {assessment?.assessed_by_name && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Assessed by</span>
            <span>{assessment.assessed_by_name}</span>
          </div>
        )}

        {/* Threshold */}
        {assessment?.threshold_days != null && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Reassessment threshold</span>
            <span>{assessment.threshold_days} days</span>
          </div>
        )}

        {/* Comment */}
        {assessment?.comment && (
          <div className="text-sm">
            <span className="text-muted-foreground">Comment: </span>
            <span className="text-foreground">{assessment.comment}</span>
          </div>
        )}

        {/* Record / form toggle */}
        {recording ? (
          <RecordAssessmentForm
            assessmentType={assessmentType}
            controlId={controlId}
            slug={slug}
            onClose={() => setRecording(false)}
          />
        ) : (
          <Button variant="outline" size="sm" className="w-full" onClick={() => setRecording(true)}>
            {isNotAssessed ? 'Record First Assessment' : 'Record Assessment'}
          </Button>
        )}
      </div>
    </div>
  )
}

// --- Inline record form ---

function RecordAssessmentForm({
  assessmentType,
  controlId,
  slug,
  onClose,
}: {
  assessmentType: string
  controlId: string
  slug: string
  onClose: () => void
}) {
  const api = useApi()
  const ctrlApi = controlsApi(api)
  const queryClient = useQueryClient()

  const [effectiveness, setEffectiveness] = useState<string>('')
  const [assessedDate, setAssessedDate] = useState(new Date().toISOString().slice(0, 10))
  const [comment, setComment] = useState('')

  const mutation = useMutation({
    mutationFn: (data: RecordAssessmentRequest) => ctrlApi.recordAssessment(controlId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.assessments(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.assessmentHistory(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.detail(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.history(slug, controlId) })
      onClose()
    },
  })

  function handleSubmit() {
    if (!effectiveness || !assessedDate) return
    mutation.mutate({
      assessment_type: assessmentType,
      effectiveness_state: effectiveness,
      assessed_date: assessedDate,
      comment: comment || undefined,
    })
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="text-xs font-medium text-muted-foreground">Record new assessment</div>

      {/* Effectiveness picker */}
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Effectiveness</label>
        <div className="flex gap-2">
          {effectivenessOptions.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setEffectiveness(opt)}
              className={cn(
                'flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors',
                effectiveness === opt
                  ? effectivenessButtonActive(opt)
                  : 'bg-background hover:bg-muted/50',
              )}
            >
              {effectivenessLabels[opt]}
            </button>
          ))}
        </div>
      </div>

      {/* Date */}
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Assessment date</label>
        <input
          type="date"
          value={assessedDate}
          onChange={(e) => setAssessedDate(e.target.value)}
          className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
        />
      </div>

      {/* Comment */}
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Comment (optional)</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
          placeholder="Observations, findings, or notes…"
        />
      </div>

      {/* Error */}
      {mutation.error && (
        <p className="text-xs text-red-600">{(mutation.error as Error).message}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="flex-1"
          disabled={!effectiveness || !assessedDate || mutation.isPending}
          onClick={handleSubmit}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

// --- Assessment History Dialog ---

function AssessmentHistoryDialog({
  open,
  onClose,
  controlId,
  slug,
}: {
  open: boolean
  onClose: () => void
  controlId: string
  slug: string
}) {
  const api = useApi()
  const ctrlApi = controlsApi(api)

  const { data, error, isLoading } = useQuery({
    queryKey: queryKeys.controls.assessmentHistory(slug, controlId),
    queryFn: () => ctrlApi.assessmentHistory(controlId),
    enabled: open,
  })

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Assessment History (Last 12 Months)</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading && <TableSkeleton rows={5} />}

          {error && (
            <ErrorState
              message="Failed to load assessment history"
              technicalDetail={(error as Error).message}
            />
          )}

          {data && data.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No assessment history found for this control in the last 12 months.
            </p>
          )}

          {data && data.length > 0 && (
            <div className="space-y-1">
              {/* Table header */}
              <div className="grid grid-cols-[160px_1fr_120px_120px] gap-2 border-b px-2 py-2 text-xs font-medium text-muted-foreground">
                <span>Recorded</span>
                <span>Assessment Type</span>
                <span>Result</span>
                <span>Assessed By</span>
              </div>

              {/* Rows */}
              {data.map((entry) => (
                <AssessmentHistoryRow key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AssessmentHistoryRow({ entry }: { entry: AssessmentHistoryEntry }) {
  return (
    <div className="group grid grid-cols-[160px_1fr_120px_120px] gap-2 rounded px-2 py-2 text-sm hover:bg-muted/50">
      {/* Changed at */}
      <span className="text-muted-foreground">
        {new Date(entry.changed_at).toLocaleDateString()}{' '}
        <span className="text-xs">{new Date(entry.changed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </span>

      {/* Assessment type */}
      <div className="flex flex-col">
        <span className="font-medium">
          {assessmentTypeFullLabels[entry.assessment_type] ?? entry.assessment_type}
        </span>
        {entry.comment && (
          <span className="text-xs text-muted-foreground line-clamp-1">{entry.comment}</span>
        )}
      </div>

      {/* Effectiveness */}
      <div className="flex items-center gap-1.5">
        {effectivenessSmallIcon(entry.effectiveness_state)}
        <Badge variant="secondary" className={cn('text-xs', effectivenessBadgeStyle(entry.effectiveness_state))}>
          {effectivenessLabels[entry.effectiveness_state] ?? entry.effectiveness_state}
        </Badge>
      </div>

      {/* Assessed by */}
      <span className="truncate text-muted-foreground">
        {entry.assessed_by_name || '—'}
      </span>
    </div>
  )
}

function effectivenessButtonActive(state: string): string {
  switch (state) {
    case 'effective':
      return 'border-green-400 bg-green-50 text-green-700'
    case 'partially_effective':
      return 'border-amber-400 bg-amber-50 text-amber-700'
    case 'ineffective':
      return 'border-red-400 bg-red-50 text-red-700'
    default:
      return 'border-primary bg-primary/5'
  }
}
